/**
 * useResourceCreate — POST a new row, invalidate list cache.
 *
 * Contract: POST /api/<resource> body → 201 + created row
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@/lib/api";

export function useResourceCreate<T = unknown>(resource: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: Record<string, unknown>): Promise<T> => {
      const res = await api(`/api/${encodeURIComponent(resource)}`, {
        method: "POST",
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`POST /api/${resource} → ${res.status}${text ? `: ${text}` : ""}`);
      }
      return (await res.json()) as T;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [resource, "list"] });
    },
  });
}
